import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Button,
  PageHeader,
  ReadMode,
} from '@cochlear-design-system/foundation';
import { DataTable } from '@cochlear-design-system/features.dataTable/dist/DataTable/DataTable';
import { getLabelsForFields } from '../../utils/labelHelper';
import { getPatientTableConfig } from './configs/assignLoanerConfig';
import { patientData } from './mappers/dataMapper';
import { getSimpleSearchConfig } from '../Search/searchConfig';
import { routes } from '../../config';
import { SimpleSearch } from '../../ui/Search/SimpleSearch';
import providerService from '../../services/providerService';
import patientService from '../../services/patientService';
import AssignLoanerDeceasedView from './components/AssignLoanerDeceasedView';
import { logger } from '../../logger';

const useQueryStrings = () =>
  new URLSearchParams(useLocation().search);

const useLabels = (rendering) =>
  useMemo(
    () => getLabelsForFields(rendering.fields),
    [rendering.fields],
  );

const AssignLoaner = ({ rendering, country, lang }) => {
  const location = useLocation();
  const history = useHistory();
  const queryStrings = useQueryStrings();
  const labels = useLabels(rendering);

  const selectedLoaner = location.state?.selectedLoaner;
  const [renderState, setRenderState] = useState();
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState(queryStrings.get('q'));

  const currentProvider = providerService.getCurrentProvider();
  const providerId = currentProvider?.id;
  const providerName = currentProvider?.name;
  const log = logger();

  const [isDeceased, setIsDeceased] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const isProcessingRef = useRef(false);
  const [deceasedPatientName, setDeceasedPatientName] = useState('');

  const handleAssignLoaner = useCallback(
    async (patientId) => {
      if (isProcessingRef.current) return;

      const patient = tableData.find(
        (p) => p.cochlearId === patientId,
      );

      if (providerId && patient?.cochlearId) {
        isProcessingRef.current = true;
        setIsProcessing(true);
        try {
          const rawPatient = await patientService.getPatient(
            providerId,
            patient.cochlearId,
          );
          if (rawPatient?.data?.isDeceased) {
            setDeceasedPatientName(patient?.name?.item || '');
            setIsDeceased(true);
            isProcessingRef.current = false;
            setIsProcessing(false);
            return;
          }
        } catch (error) {
          log.error('Failed to check deceased status', error);
        }
        isProcessingRef.current = false;
        setIsProcessing(false);
      }

      history.push({
        pathname: `/${country}/${lang}/${routes.dpx.assignLoanerDetails}`,
        state: {
          selectedLoaner,
          selectedPatient: patient,
        },
      });
    },
    [tableData, country, lang, selectedLoaner, history, providerId],
  );

  const tableConfig = useMemo(
    () => getPatientTableConfig({ handleAssignLoaner }),
    [handleAssignLoaner],
  );
  const { pageSize } = tableConfig.lazyLoadOptions;

  const fetchPatients = useCallback(
    async (page) => {
      const searchParams = {
        queryData: searchTerm?.trim(),
        countryCode: country,
      };
      const response = await providerService.getSearchResult(
        providerId,
        searchParams,
        page,
        pageSize,
      );
      if (response?.status !== 200) {
        setTableData([]);
        setRenderState('noresults');
        return;
      }
      const tableDataFormatted = patientData(response.data);
      if (tableDataFormatted?.length === 0 && page === 0) {
        setRenderState('noresults');
      } else {
        setTableData(tableDataFormatted);
        setRenderState('loaded');
      }
    },
    [country, providerId, searchTerm, pageSize],
  );

  useEffect(() => {
    tableConfig.events = {
      onLoadMore: (page) => {
        fetchPatients(page);
      },
    };
  }, [tableConfig, fetchPatients]);

  useEffect(() => {
    if (searchTerm) {
      setRenderState('loading');
      fetchPatients(0);
    }
  }, [searchTerm]);

  const searchData = useMemo(
    () => ({
      greetings: '',
      initialValues: {
        q: queryStrings.get('q') || '',
        searchType: queryStrings.get('searchType') || 'patient',
      },
    }),
    [queryStrings],
  );

  const onCancelAssignLoaner = useCallback(() => {
    history.push(`/${country}/${lang}/${routes.dpx.clinicEquipment}`);
  }, [country, lang, history]);

  const onSearch = useCallback(async () => {
    setRenderState('loading');
    fetchPatients(0);
  }, [fetchPatients]);

  const searchConfig = useMemo(() => {
    const config = getSimpleSearchConfig(onSearch, undefined, false);
    config.onSearch = (value) => {
      setSearchTerm(value.q);
    };
    return config;
  }, [onSearch]);

  return (
    <div className="ccl__container">
      <div className="ccl__main-clinic-equipment ccl__main-assign-loaner">
        <Button
          text={labels['labels.common.back']}
          variant="tertiary"
          icon="chevron-left"
          onClick={onCancelAssignLoaner}
          className="back-button"
          iconPosition="leading"
          data-analytics="assign_loaner_back_to_clinic_equipment"
        />
        {isDeceased ? (
          <AssignLoanerDeceasedView
            deceasedPatientName={deceasedPatientName}
            labels={labels}
            country={country}
            lang={lang}
          />
        ) : (
          <>
            <PageHeader
              className="ccl__main-clinic-equipment__title"
              title={{
                content: labels['labels.assignLoaner.header.title'],
              }}
            />
            <div className="ccl__dpx_patient-wrapper">
              <ReadMode
                details={[providerName]}
                title={
                  labels[
                    'labels.assignLoaner.search.labels.searchByClinic'
                  ]
                }
              />
              <dl className="ccl__read-mode">
                <dt className="ccl__text__title ccl__text ccl__text--body-text-bold ">
                  {
                    labels[
                      'labels.assignLoaner.search.labels.searchBy'
                    ]
                  }
                </dt>
                <dd className="ccl__read-mode__details ccl__text ccl__text--body-text ">
                  <p data-nw-detail="true">
                    <SimpleSearch
                      config={searchConfig}
                      labels={labels}
                      data={searchData}
                    />
                  </p>
                </dd>
              </dl>
            </div>
            {searchTerm && (
              <div
                className={`ccl__data-table--dpx${
                  isProcessing
                    ? ' ccl__data-table--links-disabled'
                    : ''
                }`}
              >
                <DataTable
                  config={tableConfig}
                  data={tableData}
                  labels={labels}
                  stateData={{
                    parameters: { searchTerm },
                    renderState,
                  }}
                />
              </div>
            )}
            <div>
              <Button
                className="cancel-button"
                text={labels['labels.common.cancel']}
                variant="secondary"
                onClick={onCancelAssignLoaner}
                progress={2}
                data-analytics="cancel_assign_loaner"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AssignLoaner;
